import React, { useState, useEffect } from 'react';
import { Play, ArrowRight, CheckCircle, Loader, RotateCcw, Zap, X, MessageCircle } from 'lucide-react';

/**
 * Visual Flow Tab - Fancy diagram flow chart visualization for HR Recruitment
 * Shows animated A2A workflow with interactive message badges
 */
const VisualFlowTab = ({ a2aClient, isRunning, setIsRunning }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState('screening');
  const [currentStep, setCurrentStep] = useState(0);
  const [messages, setMessages] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentMessages, setAgentMessages] = useState({});

  // HR Recruitment Workflows
  const workflows = {
    sourcing: {
      id: 'sourcing',
      name: 'Candidate Sourcing',
      description: 'AI-powered candidate profile generation',
      difficulty: 'beginner',
      expectedTime: '15-30s',
      totalSteps: 6,
      steps: [
        { id: 1, from: 'recruiter', to: 'dispatch', action: 'Request Candidates', detail: 'HR requests candidate sourcing' },
        { id: 2, from: 'dispatch', to: 'sourcing', action: 'Forward to Sourcing Agent', detail: 'Dispatch routes to sourcing agent' },
        { id: 3, from: 'sourcing', to: 'ai', action: 'Generate Profiles', detail: 'DeepSeek AI creates candidate profiles' },
        { id: 4, from: 'ai', to: 'sourcing', action: 'Return Profiles', detail: 'AI provides generated candidates' },
        { id: 5, from: 'sourcing', to: 'database', action: 'Store Candidates', detail: 'Save to Supabase database' },
        { id: 6, from: 'dispatch', to: 'recruiter', action: 'Deliver Results', detail: 'Recruiter receives new candidates' }
      ]
    },
    screening: {
      id: 'screening',
      name: 'Candidate Screening',
      description: 'Intelligent resume analysis and matching',
      difficulty: 'intermediate',
      expectedTime: '20-40s',
      totalSteps: 7,
      steps: [
        { id: 1, from: 'recruiter', to: 'dispatch', action: 'Screen Candidate', detail: 'Request to screen against job requirements' },
        { id: 2, from: 'dispatch', to: 'screening', action: 'Forward to Screening Agent', detail: 'Route to screening service' },
        { id: 3, from: 'screening', to: 'database', action: 'Fetch Data', detail: 'Get candidate and job details' },
        { id: 4, from: 'screening', to: 'ai', action: 'Analyze Match', detail: 'DeepSeek analyzes candidate fit' },
        { id: 5, from: 'ai', to: 'screening', action: 'Return Analysis', detail: 'AI provides match score and insights' },
        { id: 6, from: 'screening', to: 'database', action: 'Update Status', detail: 'Save screening results' },
        { id: 7, from: 'dispatch', to: 'recruiter', action: 'Show Results', detail: 'Display screening outcome' }
      ]
    },
    scheduling: {
      id: 'scheduling',
      name: 'Interview Scheduling',
      description: 'Smart interview time slot suggestions',
      difficulty: 'beginner',
      expectedTime: '15-35s',
      totalSteps: 6,
      steps: [
        { id: 1, from: 'recruiter', to: 'dispatch', action: 'Schedule Interview', detail: 'Request interview scheduling' },
        { id: 2, from: 'dispatch', to: 'scheduling', action: 'Forward to Scheduler', detail: 'Route to scheduling agent' },
        { id: 3, from: 'scheduling', to: 'ai', action: 'Suggest Times', detail: 'DeepSeek suggests optimal time slots' },
        { id: 4, from: 'ai', to: 'scheduling', action: 'Return Suggestions', detail: 'AI provides time options' },
        { id: 5, from: 'scheduling', to: 'database', action: 'Create Interview', detail: 'Save interview details' },
        { id: 6, from: 'dispatch', to: 'recruiter', action: 'Confirm Schedule', detail: 'Interview scheduled successfully' }
      ]
    }
  };

  const currentWorkflow = workflows[selectedWorkflow];

  // Agent configurations
  const agents = {
    recruiter: {
      name: 'HR Recruiter',
      nameChinese: '招聘经理',
      icon: '👨‍💼',
      color: 'from-blue-400 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      position: 'left'
    },
    dispatch: {
      name: 'Dispatch Agent',
      nameChinese: '调度 Agent',
      icon: '🤖',
      color: 'from-purple-400 to-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-300',
      position: 'center'
    },
    sourcing: {
      name: 'Sourcing Agent',
      nameChinese: '招聘 Agent',
      icon: '🔍',
      color: 'from-green-400 to-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      position: 'right'
    },
    screening: {
      name: 'Screening Agent',
      nameChinese: '筛选 Agent',
      icon: '📋',
      color: 'from-orange-400 to-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-300',
      position: 'right'
    },
    scheduling: {
      name: 'Scheduling Agent',
      nameChinese: '排程 Agent',
      icon: '📅',
      color: 'from-pink-400 to-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-300',
      position: 'right'
    },
    ai: {
      name: 'DeepSeek AI',
      nameChinese: 'AI 引擎',
      icon: '🧠',
      color: 'from-yellow-400 to-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-300',
      position: 'far-right'
    },
    database: {
      name: 'Supabase DB',
      nameChinese: '数据库',
      icon: '💾',
      color: 'from-indigo-400 to-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-300',
      position: 'far-right'
    }
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (isAnimating && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isAnimating, startTime]);

  // Run workflow animation
  const runWorkflow = async () => {
    setIsAnimating(true);
    setIsRunning(true);
    setCurrentStep(0);
    setMessages([]);
    setStartTime(Date.now());

    const steps = currentWorkflow.steps;

    for (let i = 0; i < steps.length; i++) {
      setCurrentStep(i + 1);
      const step = steps[i];
      
      const newMessage = {
        id: step.id,
        from: step.from,
        to: step.to,
        action: step.action,
        detail: step.detail,
        timestamp: new Date().toLocaleTimeString(),
        status: 'processing'
      };
      
      setMessages(prev => [...prev, newMessage]);
      
      setAgentMessages(prev => ({
        ...prev,
        [step.from]: [...(prev[step.from] || []), { ...newMessage, direction: 'sent' }],
        [step.to]: [...(prev[step.to] || []), { ...newMessage, direction: 'received' }]
      }));

      await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));

      setMessages(prev => prev.map(msg => 
        msg.id === step.id ? { ...msg, status: 'completed' } : msg
      ));
    }

    setIsAnimating(false);
    setIsRunning(false);
  };

  const resetWorkflow = () => {
    setCurrentStep(0);
    setMessages([]);
    setElapsedTime(0);
    setStartTime(null);
    setIsAnimating(false);
    setIsRunning(false);
    setAgentMessages({});
    setSelectedAgent(null);
  };

  const getAgentMessageCount = (agentId) => {
    return agentMessages[agentId]?.length || 0;
  };

  // Render agent avatar with interactive badge
  const AgentAvatar = ({ agentId, agent, isActive }) => {
    const messageCount = getAgentMessageCount(agentId);
    const hasMessages = messageCount > 0;
    const isSelected = selectedAgent === agentId;

    return (
      <div className="flex flex-col items-center space-y-3 z-10">
        <button
          onClick={() => hasMessages && setSelectedAgent(isSelected ? null : agentId)}
          className={`relative w-24 h-24 rounded-full bg-gradient-to-br ${agent.color} flex items-center justify-center shadow-lg transition-all ${
            isActive ? 'ring-4 ring-opacity-50 animate-pulse' : ''
          } ${
            isActive && agentId === 'recruiter' ? 'ring-blue-400' :
            isActive && agentId === 'dispatch' ? 'ring-purple-400' :
            isActive && (agentId === 'sourcing' || agentId === 'screening' || agentId === 'scheduling') ? 'ring-green-400' :
            isActive && agentId === 'ai' ? 'ring-yellow-400' :
            isActive && agentId === 'database' ? 'ring-indigo-400' : ''
          } ${
            hasMessages ? 'cursor-pointer hover:scale-105' : ''
          } ${
            isSelected ? 'ring-4 ring-indigo-600 scale-110' : ''
          }`}
        >
          <span className="text-4xl">{agent.icon}</span>
          
          {hasMessages && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold shadow-lg animate-pulse">
              {messageCount}
            </div>
          )}
          
          {hasMessages && !isSelected && (
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
              <MessageCircle className="w-4 h-4 text-indigo-600" />
            </div>
          )}
        </button>
        <div className="text-center">
          <div className="font-bold text-gray-900">{agent.nameChinese}</div>
          <div className="text-xs text-gray-500">{agent.name}</div>
          {hasMessages && (
            <div className="text-xs text-indigo-600 font-medium mt-1">
              {messageCount} message{messageCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">A2A Workflow Visualization</h2>
            <p className="text-gray-600">See real AI agents in action with animated flow diagrams</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isAnimating ? 'bg-green-100 text-green-700 animate-pulse' : 'bg-gray-100 text-gray-600'
            }`}>
              <Zap className={`w-4 h-4 inline mr-1 ${isAnimating ? 'animate-pulse' : ''}`} />
              {isAnimating ? 'LIVE AI' : 'Ready'}
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Selection */}
      <div className="card p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Select Live AI Workflow</h3>
        <div className="grid grid-cols-3 gap-4">
          {Object.values(workflows).map(workflow => (
            <button
              key={workflow.id}
              onClick={() => {
                if (!isAnimating) {
                  setSelectedWorkflow(workflow.id);
                  resetWorkflow();
                }
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedWorkflow === workflow.id
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 bg-white hover:border-indigo-300'
              } ${isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="font-bold text-gray-900 mb-1">{workflow.name}</div>
              <div className="text-sm text-gray-600 mb-2">{workflow.description}</div>
              <div className="flex items-center justify-between text-xs">
                <span className={`badge ${
                  workflow.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                  workflow.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {workflow.difficulty}
                </span>
                <span className="text-gray-500">
                  Expected: {workflow.expectedTime}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Visualization Panel */}
      <div className="card p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{currentWorkflow.name}</h3>
            <p className="text-sm text-gray-600">{currentWorkflow.description}</p>
          </div>
          <div className="flex items-center space-x-3">
            {isAnimating && (
              <div className="text-right">
                <div className="text-xs text-gray-500">Elapsed</div>
                <div className="text-lg font-bold text-indigo-600">{elapsedTime}s</div>
              </div>
            )}
            <button
              onClick={isAnimating ? resetWorkflow : runWorkflow}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                isAnimating
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg'
              }`}
            >
              {isAnimating ? (
                <>
                  <RotateCcw className="w-5 h-5" />
                  <span>Reset</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Start Live Workflow</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{currentStep} / {currentWorkflow.totalSteps}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / currentWorkflow.totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Agent Flow Diagram */}
        <div className="relative mb-12">
          <div className="text-center mb-8">
            <h4 className="text-2xl font-bold text-gray-800 mb-2">招聘流程链路</h4>
            <p className="text-sm text-gray-500">HR Recruitment Agent Communication Flow</p>
          </div>

          <div className="flex items-center justify-between relative">
            <AgentAvatar
              agentId="recruiter"
              agent={agents.recruiter}
              isActive={currentStep > 0 && (currentWorkflow.steps[currentStep - 1]?.from === 'recruiter' || currentWorkflow.steps[currentStep - 1]?.to === 'recruiter')}
            />

            <div className="flex-1 flex items-center justify-center">
              <div className={`flex items-center space-x-2 transition-all ${
                currentStep > 0 ? 'opacity-100' : 'opacity-30'
              }`}>
                <ArrowRight className={`w-8 h-8 text-indigo-600 ${currentStep > 0 ? 'animate-bounce-right' : ''}`} />
              </div>
            </div>

            <AgentAvatar
              agentId="dispatch"
              agent={agents.dispatch}
              isActive={currentStep > 0 && (currentWorkflow.steps[currentStep - 1]?.from === 'dispatch' || currentWorkflow.steps[currentStep - 1]?.to === 'dispatch')}
            />

            <div className="flex-1 flex items-center justify-center">
              <div className={`flex items-center space-x-2 transition-all ${
                currentStep > 1 ? 'opacity-100' : 'opacity-30'
              }`}>
                <ArrowRight className={`w-8 h-8 text-purple-600 ${currentStep > 1 ? 'animate-bounce-right' : ''}`} />
              </div>
            </div>

            {selectedWorkflow === 'sourcing' && (
              <AgentAvatar
                agentId="sourcing"
                agent={agents.sourcing}
                isActive={currentStep > 1 && (currentWorkflow.steps[currentStep - 1]?.from === 'sourcing' || currentWorkflow.steps[currentStep - 1]?.to === 'sourcing')}
              />
            )}
            {selectedWorkflow === 'screening' && (
              <AgentAvatar
                agentId="screening"
                agent={agents.screening}
                isActive={currentStep > 1 && (currentWorkflow.steps[currentStep - 1]?.from === 'screening' || currentWorkflow.steps[currentStep - 1]?.to === 'screening')}
              />
            )}
            {selectedWorkflow === 'scheduling' && (
              <AgentAvatar
                agentId="scheduling"
                agent={agents.scheduling}
                isActive={currentStep > 1 && (currentWorkflow.steps[currentStep - 1]?.from === 'scheduling' || currentWorkflow.steps[currentStep - 1]?.to === 'scheduling')}
              />
            )}

            <div className="flex-1 flex items-center justify-center">
              <div className={`flex items-center space-x-2 transition-all ${
                currentStep > 3 ? 'opacity-100' : 'opacity-30'
              }`}>
                <ArrowRight className={`w-8 h-8 text-gray-400 rotate-180 ${currentStep > 3 ? 'animate-bounce-left' : ''}`} />
              </div>
            </div>
          </div>

          {/* Current Action Display */}
          {currentStep > 0 && currentStep <= currentWorkflow.steps.length && (
            <div className="mt-12 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-indigo-200">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Loader className="w-6 h-6 text-indigo-600 animate-spin" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl">{agents[currentWorkflow.steps[currentStep - 1].from].icon}</span>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                    <span className="text-2xl">{agents[currentWorkflow.steps[currentStep - 1].to].icon}</span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">
                    {currentWorkflow.steps[currentStep - 1].action}
                  </h4>
                  <p className="text-gray-600">
                    {currentWorkflow.steps[currentStep - 1].detail}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Agent Message Popup Modal */}
          {selectedAgent && agentMessages[selectedAgent] && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn" onClick={() => setSelectedAgent(null)}>
              <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className={`p-6 bg-gradient-to-r ${agents[selectedAgent].color} text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-5xl">{agents[selectedAgent].icon}</span>
                      <div>
                        <h3 className="text-2xl font-bold">{agents[selectedAgent].nameChinese}</h3>
                        <p className="text-white text-opacity-90">{agents[selectedAgent].name}</p>
                        <p className="text-sm text-white text-opacity-80 mt-1">
                          {agentMessages[selectedAgent].length} message{agentMessages[selectedAgent].length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedAgent(null)}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 max-h-96 overflow-y-auto">
                  <div className="space-y-3">
                    {agentMessages[selectedAgent].map((msg, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border-l-4 ${
                          msg.direction === 'sent' 
                            ? 'bg-blue-50 border-blue-500' 
                            : 'bg-green-50 border-green-500'
                        } animate-fadeIn`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">
                              {msg.direction === 'sent' ? agents[msg.from].icon : agents[msg.to].icon}
                            </span>
                            {msg.direction === 'sent' ? (
                              <>
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                                <span className="text-lg">{agents[msg.to].icon}</span>
                              </>
                            ) : (
                              <>
                                <span className="text-lg">{agents[msg.from].icon}</span>
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                              </>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            msg.direction === 'sent' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {msg.direction === 'sent' ? 'Sent' : 'Received'}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1">{msg.action}</h4>
                        <p className="text-sm text-gray-600 mb-2">{msg.detail}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{msg.timestamp}</span>
                          <div className="flex items-center space-x-1">
                            {msg.status === 'completed' ? (
                              <>
                                <CheckCircle className="w-3 h-3 text-green-600" />
                                <span className="text-green-600">Completed</span>
                              </>
                            ) : (
                              <>
                                <Loader className="w-3 h-3 text-yellow-600 animate-spin" />
                                <span className="text-yellow-600">Processing</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => setSelectedAgent(null)}
                    className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Message Log */}
        <div className="mt-8">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-2">📋</span>
            Live Communication Log
          </h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {messages.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                Click "Start Live Workflow" to see agent communication in real-time
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex items-start space-x-3 p-3 rounded-lg border transition-all ${
                    msg.status === 'completed' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-yellow-50 border-yellow-200 animate-pulse'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {msg.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Loader className="w-5 h-5 text-yellow-600 animate-spin" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm">{agents[msg.from].icon}</span>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <span className="text-sm">{agents[msg.to].icon}</span>
                      <span className="font-medium text-gray-900 text-sm">{msg.action}</span>
                    </div>
                    <p className="text-xs text-gray-600">{msg.detail}</p>
                    <span className="text-xs text-gray-400">{msg.timestamp}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Completion Message */}
      {currentStep === currentWorkflow.totalSteps && !isAnimating && (
        <div className="card p-6 bg-green-50 border-green-200 animate-fadeIn">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Workflow Completed! 🎉</h3>
              <p className="text-gray-600 mt-1">
                All {currentWorkflow.totalSteps} steps executed successfully in {elapsedTime} seconds.
                This demonstrates real A2A agent communication in HR recruitment.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualFlowTab;

